/** @format */

import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { pricelistSchemaConstants, pricelistsReducersType } from '../../../modules/pricelists/reducers';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import EditButton from '../../../components/Buttons/EditButton';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import { roles, UserRoleHelper } from '../../../helpers/UserRoleHelper';
import Card from '../../../components/Card/Card.jsx';
import CardBody from '../../../components/Card/CardBody.jsx';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import MoneyIcon from '@material-ui/icons/Money';
import PricelistSchemaElementInterface from '../../../interfaces/billing/PricelistSchemaElementInterface';
import Pager from '../../../components/Adescom/Pager';
import { PricelistSchemaHelper } from '../../../helpers/PricelistSchemaHelper';
import CloneButton from '../../../components/Buttons/CloneButton';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { PricelistSchemaConnectorsCollectionInterface } from '../../../interfaces/api/PricelistSchemaConnectorsCollectionInterface';
import I18n from '../../../components/Translate/I18n';
import PricelistSchemaConnectorInterface from '../../../interfaces/billing/PricelistSchemaConnectorInterface';
import DeleteButton from '../../../components/Buttons/DeleteButton';
import AlertConfirm from '../../../components/Info/AlertConfirm';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import oplTableStyle from '../../../assets/themes/OPL/jss/Table/OplTableStyle';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import ExcelButton from '../../../components/Buttons/ExcelButton';

interface PricelistSchemaElementListProps {
    classes: any;
    editPricelistElement: (param: { pricelistElementId: number }) => void;
    loadPricelistSchemaElementList: (uri?: string) => void;
    clonePricelistHandler: (param: { id: number; type: string }) => void;
    setSchemaElementOrders: (orders: Array<Order>) => void;
    setSchemaElementFilters: (filters: Array<Filter>) => void;
    schemaElementOrders: Array<Order>;
    schemaElementFilters: Array<Filter>;
}

const PricelistSchemaElementList = (props: PricelistSchemaElementListProps): React.ReactElement => {
    const dispatch = useDispatch();
    const classes = props.classes;

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const { collections } = useSelector((state: { pricelistSchemaElementsReducers: pricelistsReducersType }) => {
        return state.pricelistSchemaElementsReducers;
    });

    const [alertContent, setAlertContent] = useState<string | React.ReactElement | null>(null);
    const [selectedPricelistSchemaId, setSelectedPricelistSchemaId] = useState<number | null>(null);

    const paginationCallbackFunction = (uri?: string): void => {
        props.loadPricelistSchemaElementList(uri);
    };

    const deletePricelistHandler = (param: { id: number }): void => {
        setAlertContent('general:common.loading');
        setSelectedPricelistSchemaId(param.id);
        if (param && param.id) {
            const itemLoaders = new ItemLoaders(dispatch);

            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pricelist_schema_connectors')),
                pricelistSchemaConstants.LIST,
                [{ name: 'pricelistSchemaElementId', value: param.id }],
                [],
                (data: PricelistSchemaConnectorsCollectionInterface) => {
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        setAlertContent(prepareAlertContent(data['hydra:member']));
                    } else {
                        setAlertContent('pricelists:notify.delete_pricelist_element');
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    };

    const prepareAlertContent = (data: Array<PricelistSchemaConnectorInterface>): React.ReactElement => {
        const description = (
            <Typography variant="subtitle2">
                <I18n trans={'pricelists:notify.delete_pricelist_element_active'} />
            </Typography>
        );

        const content = (
            <div style={{ marginTop: 20 }}>
                {data.map((el: PricelistSchemaConnectorInterface) => {
                    return <div key={el.pricelistSchemaId}>{el.pricelistSchema.mainname}</div>;
                })}
            </div>
        );

        return (
            <>
                {description}
                {content}
            </>
        );
    };

    const confirmDelete = (): void => {
        const itemLoader = new ItemLoaders(dispatch);

        cancelDelete();
        itemLoader.deleteCall(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pricelist_schema_elements/' + selectedPricelistSchemaId)),
            pricelistSchemaConstants.DELETE,
            (): void => {
                paginationCallbackFunction();
            },
            {
                dispatchAfterCallback: true,
                dispatchErrorAfterCallback: true,
            }
        );
    };

    const cancelDelete = (): void => {
        setAlertContent(null);
    };

    return (
        <Card>
            <CardHeader icon={<MoneyIcon />} trans="pricelists:h.pricelist_elements" />
            <CardBody>
                <GridContainer>
                    <GridItem xs={12} md={12} lg={12} style={{ marginBottom: 20 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell classes={tableStickyCommand} padding="none" align="left">
                                        <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} align="left">
                                        <TableHeaderContentCell
                                            textAlign="left"
                                            title="pricelists:h.name"
                                            updateOrderList={props.setSchemaElementOrders}
                                            updateFilterList={props.setSchemaElementFilters}
                                            fieldName="mainname"
                                            showSort={true}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={props.schemaElementFilters}
                                            sort={props.schemaElementOrders}
                                        />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} align="left">
                                        <TableHeaderContentCell textAlign="left" title="pricelists:h.vat_amount" />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {collections &&
                                    collections['hydra:member'].map((el: PricelistSchemaElementInterface) => {
                                        return (
                                            <TableRow key={el.id}>
                                                <TableCell classes={tableStickyHeader} align="left">
                                                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_PUT) && (
                                                        <EditButton
                                                            tooltipTitle="pricelists:b.edit_pricelist_element"
                                                            callback={props.editPricelistElement}
                                                            callbackParam={{ pricelistElementId: el.id }}
                                                        />
                                                    )}
                                                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_TARIFF_POST) && (
                                                        <CloneButton
                                                            tooltipTitle="pricelists:b.clone"
                                                            callback={props.clonePricelistHandler}
                                                            callbackParam={{
                                                                id: el.id,
                                                                type: PricelistSchemaHelper.PRICELIST_CLONE_TYPE.PRICELIST_SCHEMA_ELEMENT,
                                                            }}
                                                        />
                                                    )}
                                                    {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_PRICELIST_SCHEMA_ELEMENT_DELETE) && (
                                                        <DeleteButton
                                                            tooltipTitle="pricelists:b.remove"
                                                            callback={deletePricelistHandler}
                                                            callbackParam={{
                                                                id: el.id,
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell classes={tableCellRoot} align="left">
                                                    {el.mainname}
                                                </TableCell>
                                                <TableCell classes={tableCellRoot} align="left">
                                                    {el.vat}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell classes={tableCellRoot} colSpan={4}>
                                        {collections && collections['hydra:view'] && (
                                            <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </GridItem>
                </GridContainer>
            </CardBody>
            {alertContent && (
                <AlertConfirm
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                    confirmBtnText={'general:common.confirm'}
                    cancelBtnText={'general:common.cancel'}
                    content={alertContent}
                />
            )}
        </Card>
    );
};

export default withStyles(oplAppStyle)(PricelistSchemaElementList);
